import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, ClipboardCheck, Loader2, Droplets, Shirt, Layers,
  AlertTriangle, Gauge, Users, CheckCircle2, XCircle, ChevronLeft, ChevronRight,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ReferenceLine, LabelList,
} from 'recharts';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { fetchLaundryById } from '@/pages/parametrizacion/laundries/Services/laundries.services';
import { getReceptionReportTotals, getReceptionDaily } from '@/pages/reception/services/reception.services';
import { getWashingCycleReportTotals } from '@/pages/washingCycles/services/washingCycles.services';
import { getWorkedHoursSummary, getWorkedHoursDailyReport } from '@/pages/workedHours/services/workedHours.services';
import { getAlistamientosByLavanderia } from '@/pages/laundry/alistamientos/services/alistamientos.services';
import WeeklyChecklistCard from '@/pages/monitoring/components/WeeklyChecklistCard';

const n0 = (v) => Number(v || 0).toLocaleString('es-CO', { maximumFractionDigits: 0 });
const n1 = (v) => Number(v || 0).toLocaleString('es-CO', { maximumFractionDigits: 1 });
const n2 = (v) => Number(v || 0).toLocaleString('es-CO', { maximumFractionDigits: 2 });
const DIAS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

// Toda la aritmética de fechas se hace sobre strings 'YYYY-MM-DD' ancladas a
// mediodía UTC, para que sea determinística sin importar la zona del navegador
// ni si el backend agrupa en UTC o Bogotá (ambos endpoints agrupan en Bogotá).
const noonUTC = (key) => new Date(`${key}T12:00:00Z`);
const addDaysKey = (key, days) => {
  const d = noonUTC(key);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};
const dowOfKey = (key) => noonUTC(key).getUTCDay(); // 0=Dom
// Lunes de la semana que contiene a la fecha (string).
const mondayKeyOf = (key) => {
  const dow = dowOfKey(key);
  const diff = (dow === 0 ? -6 : 1) - dow;
  return addDaysKey(key, diff);
};
// "Hoy" como clave YYYY-MM-DD en zona de Colombia.
const todayBogotaKey = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Bogota' }).format(new Date());
const fmtKey = (key) => `${key.slice(8)} ${MESES[Number(key.slice(5, 7)) - 1]}`;

const Metric = ({ icon, label, value, unit, sub, color = 'text-gray-900 dark:text-white' }) => (
  <div className="p-4 rounded-xl border border-border bg-muted/30">
    <div className="flex items-center gap-2 mb-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
      {icon}{label}
    </div>
    <div className={`text-2xl font-bold tabular-nums leading-tight ${color}`}>
      {value}{unit ? <span className="text-sm font-medium ml-1 text-muted-foreground">{unit}</span> : null}
    </div>
    {sub ? <div className="text-xs text-muted-foreground mt-0.5">{sub}</div> : null}
  </div>
);

const ReporteSupervisorPage = () => {
  const { idLavanderia } = useParams();
  const { toast } = useToast();

  const [weekStartKey, setWeekStartKey] = useState(() => mondayKeyOf(todayBogotaKey()));
  const [jornada, setJornada] = useState(8);

  const [laundry, setLaundry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [daily, setDaily] = useState([]);

  // Claves de día (Lun..Dom) de la semana seleccionada.
  const weekKeys = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDaysKey(weekStartKey, i)),
    [weekStartKey],
  );

  // Rango anclado a la semana calendario de Bogotá (UTC-5 fijo, sin DST).
  const range = useMemo(() => ({
    $gte: new Date(`${weekKeys[0]}T00:00:00-05:00`).toISOString(),
    $lte: new Date(`${weekKeys[6]}T23:59:59.999-05:00`).toISOString(),
  }), [weekKeys]);

  useEffect(() => {
    (async () => {
      try {
        const l = await fetchLaundryById(idLavanderia);
        setLaundry(l);
      } catch (e) {
        toast({ title: 'Error', description: 'No se pudo cargar el centro de lavado', variant: 'destructive' });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idLavanderia]);

  const meta = Number(laundry?.indicadores?.manoDeObra?.consumoEsperado || 0);

  const loadReport = useCallback(async () => {
    if (!idLavanderia) return;
    setLoading(true);
    try {
      const commonQuery = { 'lavanderia.id': idLavanderia, createdAt: range };
      const whQuery = {
        'lavanderia.id': idLavanderia,
        fecha: range,
        'empleado.rol': { $not: { $regex: 'conductor', $options: 'i' } },
      };

      const [recResp, washResp, whResp, alistResp, recDailyResp, whDailyResp] = await Promise.all([
        getReceptionReportTotals({ query: commonQuery }).catch(() => ({})),
        getWashingCycleReportTotals({ query: commonQuery }).catch(() => ({})),
        getWorkedHoursSummary(whQuery).catch(() => ({})),
        getAlistamientosByLavanderia(idLavanderia, {
          query: { estado: { $nin: ['Pendiente', 'Eliminado'] }, fechaCreacion: range },
          page: 1, limit: 10000,
        }).catch(() => ({ data: [] })),
        getReceptionDaily({ query: commonQuery, groupBy: 'day' }).catch(() => ({})),
        getWorkedHoursDailyReport({ query: whQuery, groupBy: 'day' }).catch(() => ({})),
      ]);

      const rec = recResp?.data || {};
      const wash = washResp?.data || {};
      const wh = whResp?.data || {};
      const alist = Array.isArray(alistResp?.data) ? alistResp.data : [];

      const lenceria = alist.filter((a) => a.zona === 'Lenceria');
      const toallas = alist.filter((a) => a.zona === 'Toallas');
      const sumKg = (arr) => arr.reduce((s, a) => s + Number(a.pesoTotal || 0), 0) / 1000;
      const sumPrendas = (arr) => arr.reduce((s, a) => s + Number(a.totalItems || 0), 0);

      setData({
        kilosProcesados: Number(rec.kilosProcesados || 0),
        kilosRechazo: Number(rec.kilosRechazo || 0),
        kilosLavados: Number(wash.kilosLavados || 0),
        planchadoKg: sumKg(lenceria),
        planchadoPrendas: sumPrendas(lenceria),
        dobladoKg: sumKg(toallas),
        dobladoPrendas: sumPrendas(toallas),
        horasTrabajadas: Number(wh.totalHorasTrabajadas || 0),
        horasAutorizadas: Number(wh.totalHorasAutorizadas || 0),
      });

      // Mapas diarios por fecha (YYYY-MM-DD).
      const recMap = new Map();
      (Array.isArray(recDailyResp?.data) ? recDailyResp.data : []).forEach((it) => {
        recMap.set(String(it.date).slice(0, 10), Number(it.pesoKg ?? it.value ?? 0));
      });
      const authMap = new Map();
      const workMap = new Map();
      (Array.isArray(whDailyResp?.data) ? whDailyResp.data : []).forEach((it) => {
        const k = String(it.date).slice(0, 10);
        authMap.set(k, Number(it.totalHorasAutorizadas || 0));
        workMap.set(k, Number(it.totalHorasTrabajadas || 0));
      });

      const dias = weekKeys.map((key) => {
        const kg = recMap.get(key) || 0;
        const horasAut = authMap.get(key) || 0;
        const horasTrab = workMap.get(key) || 0;
        const efReal = horasAut > 0 ? kg / horasAut : 0;
        const horasReq = meta > 0 ? kg / meta : 0;
        return {
          label: `${DIAS[dowOfKey(key)]} ${key.slice(8)}`,
          kg, horasAut, horasTrab, efReal, horasReq,
          excesoHoras: horasAut - horasReq,
        };
      });
      setDaily(dias);
    } catch (e) {
      toast({ title: 'Error', description: e.message || 'No se pudo generar el reporte', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [idLavanderia, range, weekKeys, meta, toast]);

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idLavanderia, weekStartKey, meta]);

  const analisis = useMemo(() => {
    if (!data) return null;
    const kProc = data.kilosProcesados;
    const hAut = data.horasAutorizadas;
    const pctRechazo = kProc > 0 ? (data.kilosRechazo / kProc) * 100 : 0;
    const efReal = hAut > 0 ? kProc / hAut : 0;
    if (meta <= 0) return { pctRechazo, efReal, sinMeta: true };
    const horasReq = kProc / meta;
    const excesoHoras = hAut - horasReq;
    const jh = Number(jornada) > 0 ? Number(jornada) : 8;
    const excesoJornadas = excesoHoras / jh;
    const pctEf = ((efReal - meta) / meta) * 100;
    return { pctRechazo, efReal, horasReq, excesoHoras, excesoJornadas, pctEf, eficiente: excesoHoras <= 0, sinMeta: false };
  }, [data, meta, jornada]);

  const rechazoAlto = (analisis?.pctRechazo || 0) > 5;
  const semanaLabel = `${fmtKey(weekKeys[0])} – ${fmtKey(weekKeys[6])} ${weekKeys[6].slice(0, 4)}`;
  const shiftWeek = (delta) => setWeekStartKey((k) => addDaysKey(k, delta * 7));

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="p-4 md:p-6 lg:p-8 space-y-4">
      <div className="mb-4">
        <Button asChild variant="outline">
          <Link to={`/centros-lavado/${idLavanderia}`}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Volver al Centro de Lavado
          </Link>
        </Button>
      </div>
      <Card>
        <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-amber-500/15 p-2.5 rounded-lg">
              <ClipboardCheck className="h-7 w-7 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <CardTitle className="text-2xl">Reporte de Supervisor</CardTitle>
              <CardDescription className="mt-1">
                {laundry?.nombre ? `${laundry.nombre} — ` : ''}Rendimiento semanal y efectividad de las horas autorizadas
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => shiftWeek(-1)} aria-label="Semana anterior"><ChevronLeft className="h-4 w-4" /></Button>
              <div className="px-4 py-2 rounded-lg border border-border bg-muted/30 text-sm font-medium min-w-[220px] text-center">
                Semana: {semanaLabel}
              </div>
              <Button variant="outline" size="icon" onClick={() => shiftWeek(1)} aria-label="Semana siguiente"><ChevronRight className="h-4 w-4" /></Button>
              <Button variant="ghost" size="sm" onClick={() => setWeekStartKey(mondayKeyOf(todayBogotaKey()))}>Esta semana</Button>
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <Label className="text-xs font-medium text-muted-foreground">Horas/jornada</Label>
              <input type="number" min={1} max={24} value={jornada} onChange={(e) => setJornada(Number(e.target.value))}
                className="h-9 w-20 rounded-lg border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-amber-500/20" />
              {loading ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : null}
            </div>
          </div>
        </CardContent>
      </Card>

      <WeeklyChecklistCard laundryId={idLavanderia} weekStart={weekStartKey} />

      {!data ? (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">
          {loading ? 'Cargando…' : 'Sin datos para la semana seleccionada.'}
        </CardContent></Card>
      ) : (
        <>
          {/* Producción (semana) */}
          <Card>
            <CardHeader className="border-b border-border/50">
              <CardTitle className="text-lg">Producción de la semana</CardTitle>
              <CardDescription>Kilos procesados, lavados, planchados y doblados.</CardDescription>
            </CardHeader>
            <CardContent className="pt-4 grid grid-cols-2 lg:grid-cols-5 gap-3">
              <Metric icon={<Layers className="h-3.5 w-3.5 text-emerald-500" />} label="Procesados" value={n0(data.kilosProcesados)} unit="kg" color="text-emerald-700 dark:text-emerald-400" />
              <Metric icon={<Droplets className="h-3.5 w-3.5 text-indigo-500" />} label="Lavados" value={n0(data.kilosLavados)} unit="kg" color="text-indigo-700 dark:text-indigo-400" />
              <Metric icon={<Shirt className="h-3.5 w-3.5 text-purple-500" />} label="Planchados" value={n1(data.planchadoKg)} unit="kg" sub={`${n0(data.planchadoPrendas)} prendas`} color="text-purple-700 dark:text-purple-400" />
              <Metric icon={<Shirt className="h-3.5 w-3.5 text-blue-500" />} label="Doblados" value={n1(data.dobladoKg)} unit="kg" sub={`${n0(data.dobladoPrendas)} prendas`} color="text-blue-700 dark:text-blue-400" />
              <Metric icon={<Layers className="h-3.5 w-3.5 text-gray-500" />} label="Total alistado" value={n1(data.planchadoKg + data.dobladoKg)} unit="kg" sub={`${n0(data.planchadoPrendas + data.dobladoPrendas)} prendas`} />
            </CardContent>
          </Card>

          {/* Rechazo */}
          <Card>
            <CardHeader className="border-b border-border/50">
              <CardTitle className="text-lg">Rechazo</CardTitle>
            </CardHeader>
            <CardContent className="pt-4 grid grid-cols-2 lg:grid-cols-3 gap-3">
              <Metric icon={<AlertTriangle className="h-3.5 w-3.5 text-red-500" />} label="Kg rechazo" value={n1(data.kilosRechazo)} unit="kg" color="text-red-600 dark:text-red-400" />
              <Metric icon={<AlertTriangle className="h-3.5 w-3.5 text-red-500" />} label="% Rechazo" value={`${n1(analisis.pctRechazo)}%`}
                color={rechazoAlto ? 'text-red-600 dark:text-red-400' : 'text-emerald-700 dark:text-emerald-400'}
                sub={rechazoAlto ? 'Por encima del 5% recomendado' : 'Dentro de lo aceptable'} />
            </CardContent>
          </Card>

          {/* Efectividad diaria */}
          <Card>
            <CardHeader className="border-b border-border/50">
              <CardTitle className="text-lg">Efectividad diaria (kg/h autorizada)</CardTitle>
              <CardDescription>
                Cada barra es la eficiencia real del día. Verde = alcanzó la meta{meta > 0 ? ` (${n2(meta)} kg/h)` : ''}, rojo = por debajo.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <div style={{ width: '100%', height: 300 }}>
                <ResponsiveContainer width="100%" height="100%" debounce={50}>
                  <BarChart data={daily} margin={{ top: 18, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(v) => `${n2(v)} kg/h`} />
                    {meta > 0 ? (
                      <ReferenceLine y={meta} stroke="#16a34a" strokeDasharray="6 4" strokeWidth={2}
                        label={{ value: `Meta ${n2(meta)}`, position: 'right', fill: '#16a34a', fontSize: 11 }} />
                    ) : null}
                    <Bar dataKey="efReal" radius={[4, 4, 0, 0]}
                      shape={(props) => {
                        const { x, y, width, height, payload } = props;
                        const v = Number(payload?.efReal || 0);
                        const fill = v <= 0
                          ? '#cbd5e1'
                          : (meta > 0 && v < meta ? '#ef4444' : '#10b981');
                        return <rect x={x} y={y} width={width} height={Math.max(height, 0)} fill={fill} rx={4} ry={4} />;
                      }}>
                      <LabelList dataKey="efReal" position="top" fontSize={10} formatter={(v) => (Number(v) ? n2(v) : '')} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Horas autorizadas vs requeridas por día */}
          <Card>
            <CardHeader className="border-b border-border/50">
              <CardTitle className="text-lg">Horas autorizadas vs. requeridas por día</CardTitle>
              <CardDescription>
                {meta > 0
                  ? 'Requeridas = kg del día ÷ meta. Si lo autorizado supera lo requerido, se autorizó de más.'
                  : 'Configura la meta de kg/h para ver las horas requeridas.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <div style={{ width: '100%', height: 300 }}>
                <ResponsiveContainer width="100%" height="100%" debounce={50}>
                  <BarChart data={daily} margin={{ top: 18, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(v) => `${n1(v)} h`} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="horasAut" name="Autorizadas" fill="#0ea5e9" radius={[4, 4, 0, 0]}
                      shape={(props) => {
                        const { x, y, width, height, payload } = props;
                        const aut = Number(payload?.horasAut || 0);
                        const req = Number(payload?.horasReq || 0);
                        // Rojo cuando se autorizaron más horas de las que la meta exigía.
                        const exceeded = meta > 0 && aut > req;
                        const fill = exceeded ? '#ef4444' : '#0ea5e9';
                        return <rect x={x} y={y} width={width} height={Math.max(height, 0)} fill={fill} rx={4} ry={4} />;
                      }}>
                      <LabelList dataKey="horasAut" position="top" fontSize={10} formatter={(v) => (Number(v) ? n1(v) : '')} />
                    </Bar>
                    <Bar dataKey="horasReq" name="Requeridas (meta)" fill="#94a3b8" radius={[4, 4, 0, 0]}
                      hide={meta <= 0}>
                      <LabelList dataKey="horasReq" position="top" fontSize={10} formatter={(v) => (Number(v) ? n1(v) : '')} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Veredicto de la semana */}
          <Card>
            <CardHeader className="border-b border-border/50">
              <CardTitle className="text-lg">Productividad de la semana</CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Metric icon={<Gauge className="h-3.5 w-3.5 text-violet-500" />} label="Meta" value={meta > 0 ? n2(meta) : '—'} unit="kg/h" />
                <Metric icon={<Gauge className="h-3.5 w-3.5 text-blue-500" />} label="Eficiencia real" value={n2(analisis.efReal)} unit="kg/h"
                  color={meta > 0 ? (analisis.efReal >= meta ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400') : ''} />
                <Metric icon={<Users className="h-3.5 w-3.5 text-cyan-500" />} label="Horas autorizadas" value={n1(data.horasAutorizadas)} unit="h" sub={`Trabajadas: ${n1(data.horasTrabajadas)} h`} />
                <Metric icon={<Users className="h-3.5 w-3.5 text-gray-500" />} label="Horas requeridas (meta)" value={analisis.sinMeta ? '—' : n1(analisis.horasReq)} unit="h" />
              </div>

              {analisis.sinMeta ? (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-700 dark:text-amber-300 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>No hay meta de kg/hora configurada. Defínela en la parametrización del centro (Indicadores → Mano de Obra → consumo esperado) para evaluar la productividad.</span>
                </div>
              ) : analisis.eficiente ? (
                <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4 flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
                  <div className="text-sm">
                    <p className="font-semibold text-emerald-700 dark:text-emerald-400">Uso eficiente de la mano de obra</p>
                    <p className="text-muted-foreground mt-1">
                      Se procesaron {n0(data.kilosProcesados)} kg con {n1(data.horasAutorizadas)} h autorizadas,
                      <b> {n1(Math.abs(analisis.excesoHoras))} h por debajo</b> de las {n1(analisis.horasReq)} h que exigiría la meta
                      (≈ {n1(Math.abs(analisis.excesoJornadas))} jornadas de {jornada} h).
                      Eficiencia real <b>{n2(analisis.efReal)} kg/h</b> ({analisis.pctEf >= 0 ? '+' : ''}{n1(analisis.pctEf)}% vs meta).
                    </p>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-4 flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
                  <div className="text-sm">
                    <p className="font-semibold text-red-600 dark:text-red-400">Se autorizaron más horas de las necesarias</p>
                    <p className="text-muted-foreground mt-1">
                      Para procesar {n0(data.kilosProcesados)} kg a la meta de {n2(meta)} kg/h se requerían {n1(analisis.horasReq)} h,
                      pero se autorizaron {n1(data.horasAutorizadas)} h: <b>{n1(analisis.excesoHoras)} h de más</b>
                      {' '}(≈ <b>{n1(analisis.excesoJornadas)} jornadas</b> de {jornada} h).
                      Eficiencia real <b>{n2(analisis.efReal)} kg/h</b> ({n1(analisis.pctEf)}% vs meta).
                      Considera ajustar la autorización de personal/horas a la carga real.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </motion.div>
  );
};

export default ReporteSupervisorPage;
